/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import moment from 'moment'

import {
  Spinner,
  Warning,
  Table,
  SpanWithData,
  DisplayAmount,
  HoverCard
} from '../'

import {
  decodeBase64
} from '../../utils'

import {
  QUERY_EMPLOYER_SOLUTION_PRODUCTS
} from '../../queries'

export default class EmployerSolutionProductsTable extends React.Component {
  static propTypes = {
    solutionId: PropTypes.string.isRequired
  }

  state = {
    orderBy: 'product__family'
  }

  changeColumnSort = (orderBy) => this.setState({ orderBy })

  render() {
    let { solutionId } = this.props
    return (
      <Query
        query={QUERY_EMPLOYER_SOLUTION_PRODUCTS}
        variables={{ solution: decodeBase64(solutionId), orderBy: this.state.orderBy }}>
        {({ loading, error, data }) => {
          if (loading) {
            return <Spinner type='tail' />
          }

          if (error) {
            return <Warning>Failed to fetch data.</Warning>
          }

          let employerProducts = data.employerproducts.edges
          return (
            <div>
              {employerProducts.length === 0
                ? <Warning>No solution products found</Warning>
                : <div className='slds-scrollable_x'>
                    <Table
                      columns={['Product', 'Family', 'Active on', 'Deprecate on', 'Client cost', 'Employer cost', 'Discount incl', 'Client matching required', 'Billing type', 'Short code', 'Rules']}
                      rows={employerProducts.map(({ node: employerProduct }) => (
                        <ProductRow
                          employerProduct={employerProduct}
                          key={`productRow_${employerProduct.id}`}
                        />
                      ))}
                      objectRows={true}
                      orderBy={this.state.orderBy}
                      querySortableColumns={{
                        'Product': { orderByName: 'product__name' },
                        'Family': { orderByName: 'product__family' }
                      }}
                      changeSort={this.changeColumnSort}
                    />
                  </div>
              }
            </div>
          )
        }}
      </Query>
    )
  }
}

const ProductRow = ({ employerProduct }) => (
  <tr>
    <td data-label='Product'>
      {employerProduct.product.name}
    </td>
    <td data-label='Family'>
      <SpanWithData forField='ProductFamily' searchKey={employerProduct.product.family} />
    </td>
    <td data-label='Active on'>
      {employerProduct.activeAt
        ? moment(employerProduct.activeAt).format("DD MMM 'YY")
        : '-'
      }
    </td>
    <td data-label='Deprecate on'>
      {employerProduct.deprecateAt
        ? moment(employerProduct.deprecateAt).format("DD MMM 'YY")
        : '-'
      }
    </td>
    <td data-label='Client cost'>
      {employerProduct.costClient
        ? <DisplayAmount amountString={employerProduct.costClient} />
        : '-'
      }
    </td>
    <td data-label='Employer cost'>
      {employerProduct.costEmployer
        ? <DisplayAmount amountString={employerProduct.costEmployer} />
        : '-'
      }
    </td>
    <td data-label='Discount incl'>
      {employerProduct.discountOffBase
        ? <DisplayAmount amountString={employerProduct.discountOffBase} />
        : '-'
      }
    </td>
    <td data-label='Client matching required'>
      {employerProduct.clientMatchingRequired ? 'Yes' : 'No'}
    </td>
    <td data-label='Billing type'>
      {employerProduct.billingType
        ? <SpanWithData forField='EmployerProductBillingType' searchKey={employerProduct.billingType} />
        : '-'
      }
    </td>
    <td data-label='Short code'>
      {employerProduct.employerShortCode}
    </td>
    <td data-label='Rules' className='slds-text-align_center'>
        {employerProduct.clientMatchingRules || employerProduct.exceptionRules
          ? <HoverCard
              width={250}
              height={100}
              content={(ref, coords) => (
                <PeekRules ref={ref} coords={coords}>
                  <div className='slds-p-around_x-small' style={{ lineHeight: '1.25em' }}>
                    <b>Client matching rules:</b>
                    <span className='slds-m-left_x-small slds-m-bottom_small' >
                      {employerProduct.clientMatchingRules ? employerProduct.clientMatchingRules : 'None'}
                    </span>
                    <br />
                    <br />
                    <b>Exception rules:</b>
                    <span className='slds-m-left_x-small'>
                      {employerProduct.exceptionRules ? employerProduct.exceptionRules : 'None'}
                    </span>
                  </div>
                </PeekRules>
              )}>
              <a style={{ cursor: 'help' }}>View</a>
            </HoverCard>
          : 'None'
        }
    </td>
  </tr>
)

const PeekRules = React.forwardRef(({ coords, children }, ref) => (
  ReactDOM.createPortal(
    <aside className='hover-card-content-container product-rules u-font-size-smaller' style={coords ? { left: coords.x, top: coords.y } : {}}>
      <div ref={ref} className='hover-card-content'>
        {children}
      </div>
    </aside>,
    root
  )
))

PeekRules.propTypes = {
  coords: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
  })
}



// WEBPACK FOOTER //
// ./src/components/employer-solution-products-table/index.js